// Quick Create character view — two-step creation flow.
//
// Step 1: Pick class, species, background (vertical card list).
// Step 2: Name + token via the shared <details-step> component.
//
// On submit, buildQuickCreateRequest() assembles a full POST /api/characters
// body from the quick-create defaults lookup.

import { css, html } from 'lit';
import { api, getCDNBaseUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { FANTASY_FIRST_NAMES } from '../../lib/constants.js';
import { buildQuickCreateRequest } from '../../lib/quick-create-defaults.js';
import { router } from '../../lib/router.js';
import '../shared/details-step.js';

/**
 * @typedef {{ slug: string, name: string, description?: string }} RulesListItem
 * @typedef {{ slug: string, name: string, image_url?: string }} TokenListItem
 */

class QuickCreateView extends BaseComponent {
  static properties = {
    _step: { type: Number, state: true },
    _loading: { type: Boolean, state: true },
    _loadError: { type: String, state: true },
    _classes: { type: Array, state: true },
    _species: { type: Array, state: true },
    _backgrounds: { type: Array, state: true },
    _tokens: { type: Array, state: true },
    _tokensLoading: { type: Boolean, state: true },
    _classSlug: { type: String, state: true },
    _speciesSlug: { type: String, state: true },
    _backgroundSlug: { type: String, state: true },
    _name: { type: String, state: true },
    _tokenSlug: { type: String, state: true },
    _creating: { type: Boolean, state: true },
    _createError: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    .quick-wrap {
      padding: 1.5rem;
      max-width: 960px;
      margin: 0 auto;
      width: 100%;
      flex: 1;
    }

    .three-col {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 1.5rem;
    }

    @media (max-width: 640px) {
      .three-col {
        grid-template-columns: 1fr;
      }
    }

    .section-title {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 0.75rem;
      color: #212529;
    }

    .card-list {
      display: grid;
      grid-template-rows: repeat(4, 1fr);
      gap: 0.5rem;
    }

    .pick-card {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.75rem 1rem;
      border: 2px solid #dee2e6;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      text-align: left;
      transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease,
        background-color 0.15s ease;
    }

    .pick-card:hover {
      border-color: #0d6efd;
    }

    .pick-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.06);
    }

    .pick-card .card-name {
      font-weight: 600;
      color: #212529;
    }

    .pick-card .card-hint {
      font-size: 0.8rem;
      color: #6c757d;
    }

    .step-footer {
      margin-top: auto;
      position: sticky;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      border-top: 1px solid rgba(0, 0, 0, 0.125);
      padding: 1rem 1.25rem;
      z-index: 100;
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }

    .step-footer .btn {
      background-color: white;
    }
    .step-footer .btn-primary {
      background-color: var(--bs-btn-bg);
    }
    .step-footer .btn-outline-secondary {
      background-color: white;
    }
  `;

  constructor() {
    super();
    this._step = 1;
    this._loading = true;
    this._loadError = '';
    /** @type {RulesListItem[]} */
    this._classes = [];
    /** @type {RulesListItem[]} */
    this._species = [];
    /** @type {RulesListItem[]} */
    this._backgrounds = [];
    /** @type {TokenListItem[]} */
    this._tokens = [];
    this._tokensLoading = false;
    this._classSlug = '';
    this._speciesSlug = '';
    this._backgroundSlug = '';
    this._name = '';
    this._tokenSlug = '';
    this._creating = false;
    this._createError = '';
  }

  async connectedCallback() {
    super.connectedCallback();
    this._loadData();
  }

  async _loadData() {
    this._loading = true;
    this._loadError = '';
    try {
      const [classes, species, backgrounds] = await Promise.all([
        api.getClasses(),
        api.getSpecies(),
        api.getBackgrounds(),
      ]);
      this._classes = /** @type {RulesListItem[]} */ (classes);
      this._species = /** @type {RulesListItem[]} */ (species);
      this._backgrounds = /** @type {RulesListItem[]} */ (backgrounds);
    } catch (err) {
      console.error('Failed to load quick-create data:', err);
      this._loadError = /** @type {Error} */ (err).message || 'Failed to load options';
    } finally {
      this._loading = false;
    }
  }

  async _loadTokens() {
    if (this._tokens.length > 0) return;
    this._tokensLoading = true;
    try {
      const data = /** @type {{tokens: Array<{slug: string, image_url: string}>}} */ (
        await api.get('/api/tokens?token_type=pc&pageSize=100')
      );
      const cdnBase = getCDNBaseUrl();
      this._tokens = (data.tokens || []).map((t) => ({
        slug: t.slug,
        name: t.slug,
        image_url: `${cdnBase}/token/${t.slug}`,
      }));
    } catch (err) {
      console.error('Failed to load tokens:', err);
    } finally {
      this._tokensLoading = false;
    }
  }

  get _canAdvance() {
    return Boolean(this._classSlug) && Boolean(this._speciesSlug) && Boolean(this._backgroundSlug);
  }

  get _canSubmit() {
    return !this._creating && this._name.trim().length >= 2 && Boolean(this._tokenSlug);
  }

  _goToStep2() {
    if (!this._canAdvance) return;
    this._step = 2;
    this._loadTokens();
  }

  _goToStep1() {
    this._step = 1;
  }

  _randomizeName() {
    this._name = FANTASY_FIRST_NAMES[Math.floor(Math.random() * FANTASY_FIRST_NAMES.length)] || 'Adventurer';
  }

  _randomizeToken() {
    if (this._tokens.length === 0) return;
    const pick = this._tokens[Math.floor(Math.random() * this._tokens.length)];
    if (pick) this._tokenSlug = pick.slug;
  }

  async _onSubmit() {
    if (!this._canSubmit) return;
    this._creating = true;
    this._createError = '';
    try {
      const body = buildQuickCreateRequest({
        classSlug: this._classSlug,
        speciesSlug: this._speciesSlug,
        backgroundSlug: this._backgroundSlug,
        name: this._name.trim(),
        tokenSlug: this._tokenSlug,
      });
      const created = /** @type {{id: string}} */ (await api.createCharacter(body));
      const returnTo = router.getQuery().get('returnTo');
      if (returnTo && created?.id) {
        const separator = returnTo.includes('?') ? '&' : '?';
        router.navigate(`${returnTo}${separator}selectedCharacterId=${encodeURIComponent(created.id)}`);
      } else {
        router.navigate('/characters');
      }
    } catch (err) {
      console.error('Quick create failed:', err);
      this._createError = /** @type {Error} */ (err).message || 'Failed to create character';
    } finally {
      this._creating = false;
    }
  }

  /** @param {RulesListItem[]} items @param {string} selected @param {(slug: string) => void} onPick */
  _renderCardList(items, selected, onPick) {
    return html`
      <div class="card-list">
        ${items.map(
          (item) => html`
            <button
              type="button"
              class="pick-card ${selected === item.slug ? 'selected' : ''}"
              @click="${() => onPick(item.slug)}"
            >
              <span class="card-name">${item.name}</span>
              ${
                item.description
                  ? html`<span class="card-hint">${item.description.slice(0, 100)}${item.description.length > 100 ? '…' : ''}</span>`
                  : ''
              }
            </button>
          `,
        )}
      </div>
    `;
  }

  _renderStep1() {
    return html`
      <div class="quick-wrap">
        <p class="text-muted mb-3">Pick a class, species, and background — everything else is filled in for you.</p>
        <div class="three-col">
          <div>
            <div class="section-title">1. Class</div>
            ${this._renderCardList(this._classes, this._classSlug, (slug) => {
              this._classSlug = slug;
            })}
          </div>
          <div>
            <div class="section-title">2. Species</div>
            ${this._renderCardList(this._species, this._speciesSlug, (slug) => {
              this._speciesSlug = slug;
            })}
          </div>
          <div>
            <div class="section-title">3. Background</div>
            ${this._renderCardList(this._backgrounds, this._backgroundSlug, (slug) => {
              this._backgroundSlug = slug;
            })}
          </div>
        </div>
      </div>
      <div class="step-footer">
        <button
          type="button"
          class="btn btn-primary"
          ?disabled="${!this._canAdvance}"
          @click="${this._goToStep2}"
        >
          Next
        </button>
      </div>
    `;
  }

  _renderStep2() {
    return html`
      <div class="quick-wrap">
        <details-step
          .name="${this._name}"
          .tokenId="${this._tokenSlug}"
          .tokens="${this._tokens}"
          .tokensLoading="${this._tokensLoading}"
          .createError="${this._createError}"
          @update-name="${(/** @type {CustomEvent} */ e) => {
            this._name = e.detail.name;
          }}"
          @select-token="${(/** @type {CustomEvent} */ e) => {
            this._tokenSlug = e.detail.id;
          }}"
          @randomize-name="${this._randomizeName}"
          @randomize-token="${this._randomizeToken}"
        ></details-step>
      </div>
      <div class="step-footer">
        <button type="button" class="btn btn-outline-secondary" @click="${this._goToStep1}">Back</button>
        <button
          type="button"
          class="btn btn-primary"
          ?disabled="${!this._canSubmit}"
          @click="${this._onSubmit}"
        >
          ${this._creating ? 'Creating…' : 'Create Character'}
        </button>
      </div>
    `;
  }

  render() {
    if (this._loading) {
      return html`
        <div class="quick-wrap text-center py-5">
          <div class="spinner-border" role="status"><span class="visually-hidden">Loading…</span></div>
        </div>
      `;
    }
    if (this._loadError) {
      return html`<div class="quick-wrap"><div class="alert alert-danger">${this._loadError}</div></div>`;
    }

    return this._step === 1 ? this._renderStep1() : this._renderStep2();
  }
}

customElements.define('quick-create-view', QuickCreateView);
